import React, { useState } from 'react';
import { ApolloError, gql, useMutation } from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { describeError, getRateLimitRejection } from '../../../../common/rateLimit';

/**
 * One email to one person, written on their page (`crmEmailContact`): plain
 * text to their main address, from the dashboard's sender under the admin's
 * name, with replies to the admin. Once delivered it is kept as a note on their
 * record. Not being opted in does not stop it (opt-in is for campaigns);
 * unsubscribing, do-not-contact and an address that bounces do.
 */

export const CRM_EMAIL_CONTACT = gql`
  mutation CrmEmailContact($contactId: Int!, $subject: String!, $body: String!) {
    crmEmailContact(contactId: $contactId, subject: $subject, body: $body) {
      sentTo
      dryRun
    }
  }
`;

export const PERSONAL_LIMITS = { subject: 300, body: 20000 };

export interface Emailable {
  primaryEmail: string | null;
  emailIsBad: boolean;
  emailUnsubAt: string | null;
  doNotContact: boolean;
  mergedIntoId?: number | null;
}

/** Why this person cannot be emailed from their page, in words, or null. The API checks the same. */
export const cannotEmail = (c: Emailable): string | null => {
  if (c.mergedIntoId != null) return 'Merged into another contact: email them there.';
  if (c.doNotContact) return 'Marked do-not-contact.';
  if (!c.primaryEmail) return 'No email address. Add one with Edit details.';
  if (c.emailIsBad) return 'Email to them bounces. Correct their address first.';
  if (c.emailUnsubAt) return 'They unsubscribed from our email.';
  return null;
};

export const emailProblems = (subject: string, body: string) => {
  const out: { subject?: string; body?: string } = {};
  if (!subject.trim()) out.subject = 'Give it a subject.';
  else if (subject.trim().length > PERSONAL_LIMITS.subject) out.subject = `At most ${PERSONAL_LIMITS.subject} characters.`;
  if (!body.trim()) out.body = 'Write the email.';
  else if (body.trim().length > PERSONAL_LIMITS.body) out.body = `At most ${PERSONAL_LIMITS.body.toLocaleString('en')} characters.`;
  return out;
};

const describeSendError = (error: unknown) => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many emails at once');
  const first = error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined;
  if (first?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' || /Cannot query field/.test(first?.message ?? '')) {
    return 'The API this dashboard uses cannot email one person yet. Deploy the API first.';
  }
  if (first?.message) return first.message;
  return 'The email could not be sent. Nothing was sent.';
};

interface EmailPersonDialogProps {
  contactId: number;
  /** Their first name, or their name: for the title and greeting. */
  name: string;
  email: string;
  onClose: () => void;
  onSent: (dryRun: boolean) => void;
}

export const EmailPersonDialog = ({ contactId, name, email, onClose, onSent }: EmailPersonDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(() => `Hi ${name},\n\n`);
  const [tried, setTried] = useState(false);
  const [send, { loading, error }] = useMutation<{ crmEmailContact: { sentTo: Array<string>; dryRun: boolean } }>(CRM_EMAIL_CONTACT);
  const problems = emailProblems(subject, body);

  const go = async () => {
    setTried(true);
    if (Object.keys(problems).length) return;
    try {
      const { data } = await send({ variables: { contactId, subject: subject.trim(), body: body.trim() } });
      if (data) onSent(data.crmEmailContact.dryRun);
    } catch {
      // Shown below.
    }
  };

  return (
    <Dialog open onClose={loading ? undefined : onClose} fullScreen={phone} fullWidth maxWidth="sm" aria-labelledby="crm-email-person-title">
      <DialogTitle id="crm-email-person-title">Email {name}</DialogTitle>
      <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            To
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
            {email}
          </Typography>
        </Box>
        <TextField
          label="Subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          error={tried && !!problems.subject}
          helperText={tried && problems.subject}
          inputProps={{ maxLength: PERSONAL_LIMITS.subject }}
          autoFocus
          disabled={loading}
        />
        <TextField
          label="Message"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          error={tried && !!problems.body}
          helperText={(tried && problems.body) || 'Plain text. A blank line starts a new paragraph.'}
          multiline
          minRows={8}
          maxRows={20}
          disabled={loading}
        />
        <Typography variant="caption" color="text.secondary">
          It goes from the dashboard’s address under your name, and replies come to you. A copy is kept as a note on their record.
        </Typography>
        {error && <Alert severity="error">{describeSendError(error)}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={go} disabled={loading} startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};
